/* global Excel */
import { colorFor, tintColor } from "../utils/presenceColors";

// Colleagues' selected BOQ rows are tinted in the sheet while the BOQ tab is open, and put back afterwards.
const STORE_KEY = "boq.presenceMarks";
const SCAN_ROWS = 600;
const NUMBER_RE = /^\d+(\.\d+)*$/u;

let selectionHandler = null;
let applied = []; // [{ sheet, row, col, original, mark }]
let chain = Promise.resolve();
let lastSignature = null; // who is on which row in the last round, to skip identical rounds

const hasExcel = () => typeof Excel !== "undefined" && typeof Excel.run === "function";
const enqueue = (fn) => {
  chain = chain.then(fn).catch(() => undefined);
  return chain;
};

const persist = () => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(applied)); } catch { /* ignore */ }
};

const readStored = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || "[]"); } catch { return []; }
};

// Detail sheets keep the row number in column A, the TOTAL sheet in column B.
const rowKey = (a, b) => String(a ?? "").trim() || String(b ?? "").trim();

/** The BOQ row the user has selected: { sheet, label } or null (not on a numbered row). */
async function readSelectionFocus(isBoqSheet) {
  return Excel.run(async (ctx) => {
    const range = ctx.workbook.getSelectedRange();
    const sheet = range.worksheet;
    range.load("rowIndex");
    sheet.load("name");
    await ctx.sync();
    if (isBoqSheet && !isBoqSheet(sheet.name)) return null;
    const cells = sheet.getRangeByIndexes(range.rowIndex, 0, 1, 2);
    cells.load("values");
    await ctx.sync();
    const label = rowKey(cells.values[0][0], cells.values[0][1]);
    return NUMBER_RE.test(label) ? { sheet: sheet.name, label } : null;
  });
}

/** Call onFocus({ sheet, label } | null) now and whenever the selection moves. */
export async function startSelectionWatch(onFocus, isBoqSheet) {
  if (!hasExcel()) return;
  await stopSelectionWatch();
  const report = async () => {
    try { onFocus(await readSelectionFocus(isBoqSheet)); } catch { /* selection on a chart etc. */ }
  };
  try {
    await Excel.run(async (ctx) => {
      selectionHandler = ctx.workbook.onSelectionChanged.add(report);
      await ctx.sync();
    });
  } catch {
    selectionHandler = null;
  }
  await report();
}

export async function stopSelectionWatch() {
  const handler = selectionHandler;
  selectionHandler = null;
  if (!handler || !hasExcel()) return;
  try {
    await Excel.run(handler.context, async (ctx) => {
      handler.remove();
      await ctx.sync();
    });
  } catch { /* ignore */ }
}

// Put back the colour of every cell we tinted (only if it still carries our tint).
async function restore(ctx, list) {
  if (!list.length) return;
  const sheets = {};
  list.forEach((m) => {
    if (!sheets[m.sheet]) {
      sheets[m.sheet] = ctx.workbook.worksheets.getItemOrNullObject(m.sheet);
      sheets[m.sheet].load("isNullObject");
    }
  });
  await ctx.sync();
  const cells = list.map((m) => {
    const ws = sheets[m.sheet];
    if (ws.isNullObject) return null;
    const cell = ws.getRangeByIndexes(m.row, m.col, 1, 1);
    cell.format.fill.load("color");
    return cell;
  });
  await ctx.sync();
  list.forEach((m, i) => {
    const cell = cells[i];
    if (!cell || String(cell.format.fill.color).toUpperCase() !== m.mark) return;
    if (m.original && m.original !== "#FFFFFF") cell.format.fill.color = m.original;
    else cell.format.fill.clear();
  });
  await ctx.sync();
}

/** Remove all our tints (also leftovers from a session that was closed without cleaning up). */
export function clearPresenceMarks() {
  if (!hasExcel()) return Promise.resolve();
  return enqueue(async () => {
    const list = applied.length ? applied : readStored();
    applied = [];
    lastSignature = null;
    persist();
    await Excel.run((ctx) => restore(ctx, list));
  });
}

/** Tint the row each colleague has selected. others: [{ user_id, focus: { sheet, label } | null }] */
export function syncPresenceMarks(others) {
  if (!hasExcel()) return Promise.resolve();
  return enqueue(async () => {
    const wanted = (others || []).filter((o) => o?.focus?.sheet && o.focus.label);
    const signature = wanted.map((o) => `${o.user_id}|${o.focus.sheet}|${o.focus.label}`).sort().join(";");
    if (signature === lastSignature) return; // nothing moved since the last round
    lastSignature = signature;

    await Excel.run(async (ctx) => {
      await restore(ctx, applied.length ? applied : readStored());
      applied = [];

      const names = [...new Set(wanted.map((o) => o.focus.sheet))];
      const sheets = {};
      names.forEach((n) => {
        sheets[n] = ctx.workbook.worksheets.getItemOrNullObject(n);
        sheets[n].load("isNullObject");
      });
      await ctx.sync();

      const scans = {};
      names.forEach((n) => {
        if (sheets[n].isNullObject) return;
        scans[n] = sheets[n].getRangeByIndexes(0, 0, SCAN_ROWS, 2);
        scans[n].load("values");
      });
      await ctx.sync();

      const seen = new Set();
      const targets = [];
      wanted.forEach((o) => {
        const scan = scans[o.focus.sheet];
        if (!scan) return;
        const row = scan.values.findIndex((v) => rowKey(v[0], v[1]) === String(o.focus.label));
        if (row < 0) return;
        const mark = tintColor(colorFor(o.user_id));
        [0, 1].forEach((col) => {
          const key = `${o.focus.sheet}!${row}!${col}`;
          if (seen.has(key)) return;
          seen.add(key);
          const cell = sheets[o.focus.sheet].getRangeByIndexes(row, col, 1, 1);
          cell.format.fill.load("color");
          targets.push({ sheet: o.focus.sheet, row, col, mark, cell });
        });
      });
      await ctx.sync();

      applied = targets.map((t) => ({
        sheet: t.sheet, row: t.row, col: t.col, mark: t.mark,
        original: String(t.cell.format.fill.color || "").toUpperCase(),
      }));
      persist();
      targets.forEach((t) => { t.cell.format.fill.color = t.mark; });
      await ctx.sync();
    });
  });
}
